/**
 * L2: one read of the client-portal settings change history for the whole Settings page; `reload`
 * after each save. A failed read never blocks the settings: the cards say it couldn't be loaded.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { getSettingsHistory, type SettingsHistoryCards, type SettingsHistoryEntry } from '@/lib/portalSettingsHistoryApi';

/** What the page hands each section: the cards once read (null before, or when unreadable). */
export interface SettingsHistoryState { cards: SettingsHistoryCards | null; unavailable: boolean }

export function useSettingsHistory() {
  const [history, setHistory] = useState<SettingsHistoryState>({ cards: null, unavailable: false });
  // Only the newest read may set the state: an older one answering later is dropped.
  const latest = useRef(0);
  const reload = useCallback(() => {
    const ticket = ++latest.current;
    return getSettingsHistory().then(
      (cards) => { if (ticket === latest.current) setHistory({ cards, unavailable: false }); },
      // Never leave older lines looking current after a save: say it couldn't be read.
      () => { if (ticket === latest.current) setHistory({ cards: null, unavailable: true }); },
    );
  }, []);
  useEffect(() => { void reload(); }, [reload]);
  return { history, reload };
}

/** A card's entries from the page's history (undefined while it loads or when unreadable). */
export const entriesFor = (state: SettingsHistoryState | undefined, key: string): SettingsHistoryEntry[] | undefined =>
  state?.cards ? (state.cards[key] ?? []) : undefined;
